/**
 * Asset Creation - Step 3: Complete
 *
 * Final step in the asset creation flow.
 * Shows a summary of the created asset and suggested next actions.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Button, Stack, Text, Title } from '@mantine/core'
import { IconExternalLink, IconPlus } from '@tabler/icons-react'
import type { SuggestedProjectTemplate } from '@/features/projects/components/ProjectSuggestionModal'
import type { CatalogSuggestedProject } from '@/features/assets/types/assetTypes'
import { useAssetCreation } from '@/features/assets/context/AssetCreationContext'
import { AssetCreationSummary } from '@/features/assets/components/AssetCreationSummary'
import { useWorkflowNavigation } from '@/components/common'
import { ProjectSuggestionModal } from '@/features/projects/components/ProjectSuggestionModal'

export const Route = createFileRoute('/_authed/assets/new/complete')({
  component: CompleteStep,
})

function CompleteStep() {
  const navigate = useNavigate()
  const { selectedCatalog, attributes, creationResult, reset } = useAssetCreation()
  const { updatePrimaryButton, updateSecondaryButton, updateAdditionalButtons } = useWorkflowNavigation()

  // Suggested project modal: open modal instead of creating directly
  const [suggestedProjectForModal, setSuggestedProjectForModal] = useState<CatalogSuggestedProject | null>(null)
  const [createdProjectIds, setCreatedProjectIds] = useState<Set<string>>(new Set())

  // Redirect to first step if no creation result
  if (!creationResult) {
    return (
      <Stack gap="md">
        <Title order={3}>No asset created</Title>
        <Text c="dimmed">Please start over to create an asset.</Text>
        <Button onClick={() => navigate({ to: '/assets/new', search: { parentId: undefined } })}>Start Over</Button>
      </Stack>
    )
  }

  const dueDateFromAttributes = useMemo(() => {
    const birthDate = attributes.birth_month_year ?? attributes.birth_date
    if (birthDate && typeof birthDate === 'string') {
      const [year, month] = birthDate.split('-').map(Number)
      if (year && month) return new Date(year, month - 1, 1).getTime()
    }
    return undefined
  }, [attributes.birth_month_year, attributes.birth_date])

  const handleStartProject = useCallback((_templateId: string, project: CatalogSuggestedProject) => {
    setSuggestedProjectForModal(project)
  }, [])

  const templateForModal: SuggestedProjectTemplate | null = suggestedProjectForModal
    ? {
        templateId: suggestedProjectForModal.templateId,
        title: suggestedProjectForModal.title,
        description: suggestedProjectForModal.description,
        icon: suggestedProjectForModal.icon,
        difficulty: suggestedProjectForModal.difficulty,
        estimatedHours: suggestedProjectForModal.estimatedHours,
        estimatedCost: suggestedProjectForModal.estimatedCost,
        taskCount: suggestedProjectForModal.tasks.length,
        previewTasks: suggestedProjectForModal.tasks.map((t) => ({
          title: t.title,
          description: t.description,
          relativeDueDate: t.relativeDueDate,
        })),
      }
    : null

  const handleQueueItemComplete = () => {
    // Queue items will automatically refresh via Convex queries
  }

  const handleViewAsset = useCallback(() => {
    if (!creationResult.assetId) return
    
    const assetId = creationResult.assetId
    // Navigate to asset detail page
    // Don't reset context here - let the component unmount naturally
    // The context will be reset when starting a new creation flow
    navigate({ 
      to: '/assets/$id', 
      params: { id: assetId as string },
      replace: true // Replace the creation flow in history
    })
  }, [creationResult, navigate])

  const handleCreateAnother = useCallback(() => {
    reset()
    navigate({ to: '/assets/new', search: { parentId: undefined } })
  }, [reset, navigate])

  // Use refs to store latest callbacks to avoid infinite loops
  const handleViewAssetRef = useRef(handleViewAsset)
  const handleCreateAnotherRef = useRef(handleCreateAnother)
  handleViewAssetRef.current = handleViewAsset
  handleCreateAnotherRef.current = handleCreateAnother

  // Configure navigation buttons
  useEffect(() => {
    updateSecondaryButton({
      label: 'Add Another Asset',
      onClick: () => handleCreateAnotherRef.current(),
      variant: 'subtle',
      leftSection: <IconPlus size={16} />,
    })

    updatePrimaryButton({
      label: `View ${creationResult.assetName || 'Asset'}`,
      onClick: () => handleViewAssetRef.current(),
      leftSection: <IconExternalLink size={16} />,
    })

    updateAdditionalButtons([])

    return () => {
      updateSecondaryButton(undefined)
      updatePrimaryButton(undefined)
      updateAdditionalButtons([])
    }
  }, [creationResult, updatePrimaryButton, updateSecondaryButton, updateAdditionalButtons])

  return (
    <>
      <AssetCreationSummary
        creationResult={creationResult}
        suggestedProjects={selectedCatalog?.suggestedProjects}
        onStartProject={handleStartProject}
        createdProjectIds={createdProjectIds}
        creatingProjectId={null}
        showSuggestedProjects={true}
        onQueueItemComplete={handleQueueItemComplete}
      />
      {templateForModal && creationResult.assetId && (
        <ProjectSuggestionModal
          opened={!!suggestedProjectForModal}
          onClose={() => setSuggestedProjectForModal(null)}
          template={templateForModal}
          assetId={creationResult.assetId}
          dueDate={dueDateFromAttributes}
          onSuccess={(templateId) =>
            setCreatedProjectIds((prev) => new Set([...prev, templateId]))
          }
        />
      )}
    </>
  )
}
